/** @graphql cc **/
import React from 'react';
import gql from 'graphql-tag';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getContentMode } from '@confluence/content-types-utils/entry-points/getContentMode.ts';
import {
	VALID_OPERATION_TARGETS,
	type ContentType,
} from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { ContentTypeClassificationLoader } from '@confluence/data-classification/entry-points/ContentTypeClassification.tsx';
import { ClassificationEditModalLazyLoader as ClassificationEditModal } from '@confluence/data-classification/entry-points/ClassificationEditModal.ts';
import { TagButtonUIVariant } from '@confluence/data-classification/entry-points/tagButtonUIVariant.ts';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isExternalShareRequest } from '@confluence/external-share-utils/entry-points/isExternalShareRequest.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { fg } from '@confluence/feature-gating';

import type {
	BylineClassificationQuery,
	BylineClassificationQueryVariables,
} from './__types__/BylineClassificationQuery.ts';

type BylineClassificationProps = {
	contentId: string;
};

export const BylineClassification = ({ contentId }: BylineClassificationProps) => {
	// reusing the contentypes header query since it'll be cached already
	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data, error: BylineClassificationQueryError } = useQueryInterop<
		BylineClassificationQuery,
		BylineClassificationQueryVariables
	>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		gql`
			query BylineClassificationQuery($contentId: ID) {
				content(id: $contentId, status: ["archived", "current", "draft"]) {
					nodes {
						id
						title
						operations {
							operation
							targetType
						}
						space {
							id
							name
							key
							alias
						}
						type
						status
					}
				}
			}
		`,
		{
			variables: { contentId },
		},
	);
	const contentData = data?.content?.nodes?.[0];
	const spaceKey = contentData?.space?.key || '';
	const isExternal = isExternalShareRequest();

	// Different pieces of content can have different modes, which need to be reflected in this header.
	// Eg, pages & blogposts can be 'draft', 'archived', 'view', 'edit', or 'live' (or 'embedded', or 'legacy'!)
	const rawContentType = contentData?.type ?? undefined; // ShareButton's shareContentType prop doesn't like null values
	const isArchived = contentData?.status === 'archived';
	const isDraft = contentData?.status === 'draft';
	const isViewEditOrLive = useGetPageMode();

	const contentType = {
		type: rawContentType,
		mode: getContentMode(rawContentType, isArchived, isDraft, isViewEditOrLive),
	};

	let contentStatusContext;
	if (
		contentType.mode === 'live' ||
		(contentType.mode === PageMode.VIEW && fg('dlp_classification_access'))
	) {
		contentStatusContext = 'current-and-draft' as const;
	} else if (contentType.mode === 'edit' || contentType.mode === 'draft') {
		contentStatusContext = 'draft-only' as const;
	} else {
		contentStatusContext = 'current-only' as const;
	}

	const isViewModeReadOnly = contentType.mode === PageMode.VIEW && !fg('dlp_classification_access');

	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: contentData?.space?.key,
		contentStatusContext,
		shouldReadFromQueries: true,
	});

	const hasUpdatePermission = fg('dlp_classification_access')
		? (contentClassificationMetadata.isReclassificationPermitted ?? false)
		: contentData?.operations?.some(
				(operation) =>
					operation?.targetType &&
					VALID_OPERATION_TARGETS.includes(operation?.targetType as ContentType) &&
					operation?.operation === 'update',
			);

	const readOnly = isExternal || !hasUpdatePermission || isViewModeReadOnly;

	if (BylineClassificationQueryError) {
		return <ErrorDisplay error={BylineClassificationQueryError} />;
	}

	const isClassificationEnabled = contentClassificationMetadata?.featureEnabled ?? false;

	return isClassificationEnabled ? (
		<>
			<ContentTypeClassificationLoader
				variant={TagButtonUIVariant.default}
				spaceKey={spaceKey}
				contentId={contentId}
				contentType={contentType.type ?? ''}
				contentClassificationMetadata={contentClassificationMetadata}
				readOnly={readOnly}
				spacing="expanded"
			/>

			<ClassificationEditModal
				contentId={contentId}
				spaceKey={spaceKey}
				contentClassificationMetadata={contentClassificationMetadata}
			/>
		</>
	) : null;
};
